import { useEffect, useRef, useState, type ComponentType, type SVGProps } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  FileUp,
  Settings,
  X,
  LayoutDashboard,
  KeyRound,
  Folder,
  Building2,
  Sliders,
  Users,
  ChevronDown,
  ChevronRight,
  CalendarDays,
} from 'lucide-react'
import { SIDEBAR_BY_ROLE, type Role } from '@/lib/auth'
import { cn } from '@/lib/utils'

type IconType = ComponentType<SVGProps<SVGSVGElement>>

interface NavMeta {
  to: string
  label: string
  Icon: IconType
}

/**
 * NAV_META — metadata visual de cada navId.
 *
 * F7.5: sidebar muestra solo los 4 consolidados + sub-entries Admin.
 * IDs legacy (nav-trabajadores, nav-fichas, etc.) NO aparecen aquí — el sidebar
 * los ignora silenciosamente si llegasen via SIDEBAR_BY_ROLE.
 */
const NAV_META: Record<string, NavMeta> = {
  'nav-dashboard': { to: '/app/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  'nav-expediente': { to: '/app/expediente', label: 'Expediente', Icon: Folder },
  'nav-pdfs-upload': { to: '/app/pdfs-upload', label: 'Cargar PDF', Icon: FileUp },
  'nav-admin': { to: '/app/admin', label: 'Admin', Icon: Settings },
  'nav-admin-usuarios': { to: '/app/settings/users', label: 'Usuarios', Icon: Users },
  'nav-admin-empresas': { to: '/app/admin/organizations', label: 'Empresas', Icon: Building2 },
  'nav-admin-operativos': { to: '/app/admin/operativos', label: 'Operativos', Icon: CalendarDays },
  'nav-admin-api-keys': { to: '/app/admin/api-keys', label: 'API Keys', Icon: KeyRound },
  'nav-admin-scoring-rules': {
    to: '/app/admin/scoring-rules',
    label: 'Reglas Scoring',
    Icon: Sliders,
  },
}

interface SidebarProps {
  role: Role
  /** Mobile-only: si está abierto en drawer. */
  open: boolean
  onClose: () => void
}

export function Sidebar({ role, open, onClose }: SidebarProps) {
  const items = SIDEBAR_BY_ROLE[role]
  const location = useLocation()

  // Estado de expansión de cada nav padre con children.
  // Inicialmente abierto si la ruta activa coincide con algún child.
  const initialOpen: Record<string, boolean> = {}
  for (const item of items) {
    if (item.children && item.children.length > 0) {
      const activeChild = item.children.some((cid) => {
        const meta = NAV_META[cid]
        return meta ? location.pathname.startsWith(meta.to) : false
      })
      initialOpen[item.navId] = activeChild
    }
  }
  const [expanded, setExpanded] = useState<Record<string, boolean>>(initialOpen)

  const toggle = (navId: string) => setExpanded((prev) => ({ ...prev, [navId]: !prev[navId] }))

  // F7.6 ronda 4 (Hipótesis B feedback usuario) — data-transition semántico para tests:
  //   data-state          : open / closed (cambia inmediato al togglear)
  //   data-transition     : idle / running (transitionend → idle, fallback 500ms)
  // Sin esto, el rect del aside puede no estar 100% en viewport hasta que termine el
  // paint, y bajo workers paralelos (CPU contention) eso es 1-2 frames después del
  // commit React. Tests esperan data-transition='idle' antes de interactuar.
  const [transitionState, setTransitionState] = useState<'idle' | 'running'>('idle')
  const prevOpen = useRef(open)
  useEffect(() => {
    if (prevOpen.current !== open) {
      prevOpen.current = open
      setTransitionState('running')
      // Fallback safety: si transitionend no dispara (transition-none, reduced-motion,
      // transform delta=0 en desktop), liberar el lock tras 500ms.
      const t = window.setTimeout(() => setTransitionState('idle'), 500)
      return () => window.clearTimeout(t)
    }
  }, [open])

  return (
    <>
      {/* Backdrop mobile */}
      <div
        className={cn(
          'fixed inset-0 z-30 bg-slate-900/40 transition-opacity md:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
        onClick={onClose}
        aria-hidden
      />
      <aside
        className={cn(
          // F7.6: `transition-none` explícito — Tailwind v4 inyecta transition-property
          // por default en todos los elementos. Sin override el aside anima al togglear.
          // Mantenemos data-transition lifecycle igual (fallback 500ms cubre el caso).
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-slate-200 bg-white transition-none md:static md:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
        aria-label="Navegación principal"
        data-testid="app-sidebar"
        data-state={open ? 'open' : 'closed'}
        data-transition={transitionState}
        onTransitionEnd={(e) => {
          if (e.target === e.currentTarget && e.propertyName === 'transform') {
            setTransitionState('idle')
          }
        }}
      >
        <div className="flex h-16 items-center justify-between border-b border-slate-200 px-5">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-fitlab-gradient">
              <span className="text-xs font-bold text-white">FL</span>
            </div>
            <span className="text-base font-bold tracking-tight text-slate-900">FITLAB</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-500 hover:bg-slate-100 md:hidden"
            aria-label="Cerrar menú"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto p-3" aria-label="Secciones">
          {items.map((item) => {
            const meta = NAV_META[item.navId]
            if (!meta) return null
            const { to, label, Icon } = meta
            const hasChildren = !!(item.children && item.children.length > 0)
            const isExpanded = expanded[item.navId] === true

            if (!hasChildren) {
              return (
                <NavLink
                  key={item.navId}
                  to={to}
                  data-testid={item.navId}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-[color:var(--color-fitlab-blue)]/10 text-[color:var(--color-fitlab-blue)]'
                        : 'text-slate-700 hover:bg-slate-100',
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="truncate">{label}</span>
                </NavLink>
              )
            }

            // Item con children: botón collapsible + lista hija.
            const isParentActive = (item.children ?? []).some((cid) => {
              const cMeta = NAV_META[cid]
              return cMeta ? location.pathname.startsWith(cMeta.to) : false
            })
            return (
              <div key={item.navId} className="flex flex-col">
                <button
                  type="button"
                  data-testid={item.navId}
                  onClick={() => toggle(item.navId)}
                  className={cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isParentActive
                      ? 'bg-[color:var(--color-fitlab-blue)]/10 text-[color:var(--color-fitlab-blue)]'
                      : 'text-slate-700 hover:bg-slate-100',
                  )}
                  aria-expanded={isExpanded}
                >
                  <Icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="flex-1 truncate text-left">{label}</span>
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                </button>
                {isExpanded && (
                  <div className="ml-7 mt-0.5 flex flex-col gap-0.5">
                    {(item.children ?? []).map((cid) => {
                      const cMeta = NAV_META[cid]
                      if (!cMeta) return null
                      return (
                        <NavLink
                          key={cid}
                          to={cMeta.to}
                          data-testid={cid}
                          onClick={onClose}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                              isActive
                                ? 'bg-[color:var(--color-fitlab-blue)]/10 font-medium text-[color:var(--color-fitlab-blue)]'
                                : 'text-slate-600 hover:bg-slate-100',
                            )
                          }
                        >
                          <cMeta.Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          <span className="truncate">{cMeta.label}</span>
                        </NavLink>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </nav>
        <div className="border-t border-slate-100 px-5 py-3 text-[11px] leading-tight text-slate-400">
          FITLAB · F7.5
        </div>
      </aside>
    </>
  )
}
